// TEN19 (docs/build-briefs/TEN19-publish-recipes.md; DEC-339, DEC-342 Q6 and Q7, P0.5): the pure decisions behind
// publishing a recipe from the franchisor console. No DOM, no fetch: test/ten19-publish-recipes-ui-model.test.ts proves it.
//
// A recipe has descriptive ingredient snapshots and optional shared catalog version identities. It never
// holds a franchisee's inventory item or drink id: the draft has no such field, so the request cannot carry one. The
// franchisor sees, per targeted location, a state and a date and nothing else.

import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import type { RecipeCatalogReference } from "../../src/domain/networkCatalog.ts";
import { isRecipeUnit } from "../../src/domain/recipeUnits.ts";
import { formatRecipeQuantity } from "./catalogDisplay.ts";
import {
  adoptionLabel,
  locationStateRows,
  formatStateDate,
  isLocationShape,
  type AdoptionModeDto,
  type PublicationLocationDto,
  type PublicationDto,
  type PublicationRevisionDto,
  type PublicationRowView,
  type StateView,
} from "./franchisorPublishingModel.ts";
import { sortByName } from "./nameSort.ts";

/** Only a complete, actual immutable catalog identity is accepted. */
export function isRecipeCatalogReference(value: unknown): value is RecipeCatalogReference {
  if (typeof value !== "object" || value === null) return false;
  const ref = value as Record<string, unknown>;
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return Object.keys(ref).every(key => ["catalogId", "productId", "revision"].includes(key)) && typeof ref["catalogId"] === "string" && uuid.test(ref["catalogId"]) &&
    typeof ref["productId"] === "string" && uuid.test(ref["productId"]) &&
    typeof ref["revision"] === "number" && Number.isSafeInteger(ref["revision"]) && ref["revision"] >= 1;
}

export interface RecipeSlotDto {
  catalogRef?: RecipeCatalogReference;
  position: number;
  name: string;
  category: string;
  /** Decimal text as the server stores it, for example "2.0000". */
  amount: string;
  unit: string;
  notes: string | null;
}

export interface RecipeDto {
  revision?: PublicationRevisionDto;
  /** RECUX2: build instructions. Absent or empty when the recipe has none. */
  glass?: string | null;
  garnish?: string | null;
  method?: string[];
  source?: { publicationId: string; name: string; fromName: string | null } | null;
  id: string;
  name: string;
  category: string;
  adoption: AdoptionModeDto;
  state: "published" | "withdrawn";
  createdAt: string;
  createdBy: string;
  withdrawnAt: string | null;
  withdrawnBy: string | null;
  slots: RecipeSlotDto[];
  locations: PublicationLocationDto[];
  counts: {
    accepted: number;
    declined: number;
    pending: number;
    required: number;
    needsMapping: number;
  };
}

export function isRecipeShape(value: unknown): value is RecipeDto {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  const counts = r["counts"] as Record<string, unknown> | undefined;
  return (
    typeof r["id"] === "string" &&
    typeof r["name"] === "string" &&
    typeof r["category"] === "string" &&
    (r["adoption"] === "optional" || r["adoption"] === "required") &&
    (r["state"] === "published" || r["state"] === "withdrawn") &&
    typeof r["createdAt"] === "string" &&
    typeof r["createdBy"] === "string" &&
    (r["withdrawnAt"] === null || typeof r["withdrawnAt"] === "string") &&
    (r["glass"] === undefined || r["glass"] === null || typeof r["glass"] === "string") &&
    (r["garnish"] === undefined || r["garnish"] === null || typeof r["garnish"] === "string") &&
    (r["method"] === undefined || (Array.isArray(r["method"]) && (r["method"] as unknown[]).every((step) => typeof step === "string"))) &&
    Array.isArray(r["slots"]) &&
    (r["slots"] as unknown[]).every((s) => {
      if (typeof s !== "object" || s === null) return false;
      const slot = s as Record<string, unknown>;
      return (
        (slot["catalogRef"] === undefined || isRecipeCatalogReference(slot["catalogRef"])) &&
        typeof slot["position"] === "number" &&
        typeof slot["name"] === "string" &&
        typeof slot["category"] === "string" &&
        typeof slot["amount"] === "string" &&
        typeof slot["unit"] === "string" &&
        (slot["notes"] === null || typeof slot["notes"] === "string")
      );
    }) &&
    Array.isArray(r["locations"]) &&
    (r["locations"] as unknown[]).every(isLocationShape) &&
    typeof counts === "object" &&
    counts !== null &&
    ["accepted", "declined", "pending", "required", "needsMapping"].every(
      (k) => typeof counts[k] === "number",
    )
  );
}

/** "Whiskey 2.00 oz, Simple syrup 0.25 oz", in the recipe's own order. */
export function slotsSummary(
  slots: readonly Pick<RecipeSlotDto, "name" | "amount" | "unit" | "position">[],
): string {
  return [...slots]
    .sort((a, b) => a.position - b.position)
    .map((s) => `${s.name} ${formatRecipeQuantity(s.amount)} ${s.unit}`)
    .join(", ");
}

export function recipeCountsText(recipe: Pick<RecipeDto, "adoption" | "counts">): string {
  const c = recipe.counts;
  const needs = `${c.needsMapping} needs mapping`;
  if (recipe.adoption === "required") return `${c.required} required, ${needs}`;
  return `${c.accepted} accepted, ${c.declined} declined, ${c.pending} pending, ${needs}`;
}

function recipeRowView(recipe: RecipeDto, canManage: boolean): PublicationRowView {
  const published = recipe.state === "published";
  const n = recipe.locations.length;
  return {
    id: recipe.id,
    name: recipe.name,
    kindLabel: "Recipe",
    termsText: `${recipe.category}. ${slotsSummary(recipe.slots)}`,
    adoptionText: adoptionLabel(recipe.adoption),
    windowText: null,
    stackingText: null,
    audienceText: n === 1 ? "1 location" : `${n} locations`,
    countsText: recipeCountsText(recipe),
    published,
    statusLabel: published ? "Published" : "Withdrawn",
    statusTone: published ? "good" : "neutral",
    detailText: published
      ? `Published ${formatStateDate(recipe.createdAt)} by ${recipe.createdBy}`
      : `Withdrawn ${formatStateDate(recipe.withdrawnAt)}${recipe.withdrawnBy === null ? "" : ` by ${recipe.withdrawnBy}`}`,
    canWithdraw: canManage && published,
    locations: locationStateRows(recipe),
  };
}

/** Discount rows and recipe rows in one list: published A to Z, then withdrawn A to Z (DEC-339). */
export function mergePublicationRows(
  discountRows: readonly PublicationRowView[],
  recipes: readonly RecipeDto[],
  canManage: boolean,
): PublicationRowView[] {
  const all = [...discountRows, ...recipes.map((r) => recipeRowView(r, canManage))];
  const live = sortByName(
    all.filter((r) => r.published),
    (r) => r.name,
  );
  const gone = sortByName(
    all.filter((r) => !r.published),
    (r) => r.name,
  );
  return [...live, ...gone];
}

// ---- The recipe draft -----------------------------------------------------------------------------------------

export interface SlotDraft {
  catalogRef?: RecipeCatalogReference;
  key?: string;
  name: string;
  category: string;
  amountText: string;
  unit: string;
  notes: string;
}

export interface RecipeDraft {
  name: string;
  category: string;
  slots: SlotDraft[];
  /** RECUX2: build instructions, all optional. */
  glass?: string;
  garnish?: string;
  method?: string[];
}

export function emptySlot(): SlotDraft {
  return { key: crypto.randomUUID(), name: "", category: "", amountText: "", unit: "", notes: "" };
}

export function emptyRecipeDraft(): RecipeDraft {
  return { name: "", category: "", slots: [emptySlot()] };
}

// ---- RECUX2: the redesigned Add and Edit recipe dialog (docs/build-briefs/RECUX2-franchisor-add-recipe.md) --------------------

/** The unit a new ingredient row starts on: the common case, so a unit is never blank. */
export const NEW_INGREDIENT_UNIT = "oz";
export function newIngredientRow(): SlotDraft {
  return { ...emptySlot(), unit: NEW_INGREDIENT_UNIT };
}
/** A new recipe draft for the dialog: one ingredient row on oz, one empty step, no glass or garnish. */
export function newRecipeDialogDraft(): RecipeDraft {
  return { name: "", category: "", slots: [newIngredientRow()], glass: "", garnish: "", method: [""] };
}

/** The glass pick list. The server takes any text up to 40 characters, so the list can grow without a migration. */
export const RECIPE_GLASSES = ["Rocks", "Coupe", "Highball", "Collins", "Martini", "Shot", "Mug", "Wine"] as const;
export const GARNISH_QUICK_PICKS = ["Orange peel", "Cherry", "Lime wedge", "Olive", "Lemon twist"] as const;
export const RECIPE_GLASS_MAX = 40;
export const RECIPE_GARNISH_MAX = 60;
export const RECIPE_MAX_STEPS = 20;
export const RECIPE_STEP_MAX = 300;
export const RECIPE_CATEGORY_MAX_LENGTH = 40;

/** The dialog sections, in order. The left checklist and the problem list share these numbers. */
export type RecipeSection = 1 | 2 | 3 | 4;
export const RECIPE_SECTION_TITLES: Record<RecipeSection, string> = {
  1: "Drink",
  2: "Ingredients",
  3: "How to make it",
  4: "Who gets it",
};

export type RecipeProblemTarget =
  | { kind: "name" }
  | { kind: "category" }
  | { kind: "ingredient"; index: number }
  | { kind: "amount"; index: number }
  | { kind: "locations" };
export interface RecipeProblem {
  section: RecipeSection;
  target: RecipeProblemTarget;
  /** The sentence shown under the field. */
  message: string;
  /** The short name used in the footer summary, for example "Ingredient 2". */
  label: string;
}

export const DRINK_NAME_MESSAGE = "Enter a name for the drink.";
export const CATEGORY_PICK_MESSAGE = "Choose a category.";
export const CATEGORY_NEW_MESSAGE = "Enter a name for the new category.";
export const INGREDIENT_MESSAGE = "Choose an ingredient.";
export const AMOUNT_MISSING_MESSAGE = "Enter an amount.";
export const AMOUNT_INVALID_MESSAGE = "Use a number above 0.";
export const INGREDIENT_TWICE_MESSAGE = "This ingredient is already in the recipe.";
export const LOCATIONS_PICK_MESSAGE = "Choose at least one location.";

/** True when the amount text is a usable amount (above 0, at most four decimals). */
export function amountProblem(text: string): string | null {
  if (text.trim() === "") return AMOUNT_MISSING_MESSAGE;
  return parseSlotAmount(text) === null ? AMOUNT_INVALID_MESSAGE : null;
}

/**
 * Every problem that stops a save, in the order the sections run. Pure, and called only after a save attempt (and on each edit after
 * one), so a first-time franchisor sees nothing red until Save is pressed. A legacy descriptive ingredient (a saved name with no
 * Catalog identity) is valid as it is; a NEW row must be a Catalog product (RECNET1).
 */
export function recipeDialogProblems(
  draft: RecipeDraft,
  options: { newCategory: boolean; locationsChosen: boolean },
): RecipeProblem[] {
  const problems: RecipeProblem[] = [];
  if (draft.name.trim() === "") problems.push({ section: 1, target: { kind: "name" }, message: DRINK_NAME_MESSAGE, label: "Name" });
  if (draft.category.trim() === "") {
    problems.push({
      section: 1,
      target: { kind: "category" },
      message: options.newCategory ? CATEGORY_NEW_MESSAGE : CATEGORY_PICK_MESSAGE,
      label: "Menu category",
    });
  }
  const seen = new Set<string>();
  draft.slots.forEach((slot, index) => {
    if (slot.name.trim() === "") {
      problems.push({ section: 2, target: { kind: "ingredient", index }, message: INGREDIENT_MESSAGE, label: `Ingredient ${index + 1}` });
    } else {
      const identity = slot.catalogRef ? `${slot.catalogRef.catalogId}/${slot.catalogRef.productId}` : slot.name.trim().toLowerCase();
      if (seen.has(identity)) {
        problems.push({ section: 2, target: { kind: "ingredient", index }, message: INGREDIENT_TWICE_MESSAGE, label: `Ingredient ${index + 1}` });
      }
      seen.add(identity);
    }
    const amount = amountProblem(slot.amountText);
    if (amount !== null) problems.push({ section: 2, target: { kind: "amount", index }, message: amount, label: `Amount ${index + 1}` });
  });
  if (!options.locationsChosen) {
    problems.push({ section: 4, target: { kind: "locations" }, message: LOCATIONS_PICK_MESSAGE, label: "Locations" });
  }
  return problems;
}

/** "3 things to fix before saving: Name, Menu category, Ingredient 1". Empty when there is nothing to fix. */
export function problemSummary(problems: readonly RecipeProblem[]): string {
  if (problems.length === 0) return "";
  const labels = [...new Set(problems.map((problem) => problem.label))];
  const shown = labels.slice(0, 4).join(", ");
  return `${problems.length} ${problems.length === 1 ? "thing" : "things"} to fix before saving: ${shown}${labels.length > 4 ? "..." : ""}`;
}

/** Which sections read as complete in the left checklist. How to make it is optional, so it is complete once anything is filled. */
export function sectionsComplete(
  draft: RecipeDraft,
  options: { newCategory: boolean; locationsChosen: boolean },
): Record<RecipeSection, boolean> {
  const problems = recipeDialogProblems(draft, options);
  const bad = (section: RecipeSection): boolean => problems.some((problem) => problem.section === section);
  const instructionsTouched =
    (draft.glass ?? "").trim() !== "" || (draft.garnish ?? "").trim() !== "" || (draft.method ?? []).some((step) => step.trim() !== "");
  return { 1: !bad(1), 2: !bad(2), 3: instructionsTouched, 4: !bad(4) };
}

/** The instructions as the server takes them: only what is filled, steps trimmed with blanks dropped. */
export function instructionsOf(draft: RecipeDraft): { glass?: string; garnish?: string; method?: string[] } {
  const glass = (draft.glass ?? "").trim();
  const garnish = (draft.garnish ?? "").trim();
  const method = (draft.method ?? []).map((step) => step.trim()).filter((step) => step !== "");
  return { ...(glass === "" ? {} : { glass }), ...(garnish === "" ? {} : { garnish }), ...(method.length === 0 ? {} : { method }) };
}

export type RecipeDraftField = "name" | "category" | "slots" | "locations";
export type RecipeDraftResult =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; field: RecipeDraftField; message: string; slotIndex?: number };

export const RECIPE_NAME_MISSING = "Give the recipe a name.";
export const RECIPE_NAME_TOO_LONG = "A recipe name must be 60 characters or fewer.";
export const RECIPE_CATEGORY_MISSING = "Give the recipe a category.";
export const RECIPE_SLOTS_MISSING = "Add at least one ingredient.";
export const RECIPE_LOCATIONS_MISSING = "Choose at least one location.";
export const SLOT_FIELDS_MESSAGE =
  "Every ingredient needs a name, a category, an amount and a unit.";
export const SLOT_AMOUNT_MESSAGE = "Enter an amount above 0, for example 2 or 0.25.";
export const SLOT_DUPLICATE_MESSAGE = "An ingredient is listed twice.";
export const MAX_SLOTS = 30;

/** "2" -> 2, "0.25" -> 0.25, "1.0001" -> 1.0001; null for anything else. At most four decimals, above 0, below 100000. */
export function parseSlotAmount(text: string): number | null {
  const match = /^(\d{1,5})(?:\.(\d{1,4}))?$/.exec(normalizeLeadingDot(text));
  if (match === null) return null;
  const value = Number(`${match[1]}.${match[2] ?? "0"}`);
  return value > 0 && value <= 100000 ? value : null;
}

/** The request body for a recipe draft, or the first problem with the field it belongs to. The server checks again. */
export function recipeDraftToBody(
  draft: RecipeDraft,
  adoption: AdoptionModeDto,
  selected: ReadonlySet<string>,
  allowEmptyTargets = false,
  allLocations = false,
): RecipeDraftResult {
  const name = draft.name.trim();
  if (name === "") return { ok: false, field: "name", message: RECIPE_NAME_MISSING };
  if (name.length > 60) return { ok: false, field: "name", message: RECIPE_NAME_TOO_LONG };
  const category = draft.category.trim();
  if (category === "") return { ok: false, field: "category", message: RECIPE_CATEGORY_MISSING };
  if (category.length > 40) return {ok: false, field: "category", message: "A recipe category must be 40 characters or fewer."};
  if (draft.slots.length > MAX_SLOTS) return {ok: false, field: "slots", message: `A recipe can have at most ${MAX_SLOTS} ingredients.`};
  if (draft.slots.length === 0) return { ok: false, field: "slots", message: RECIPE_SLOTS_MISSING };
  const seen = new Set<string>();
  const slots: Record<string, unknown>[] = [];
  for (const [index, slot] of draft.slots.entries()) {
    const slotName = slot.name.trim();
    const slotCategory = slot.category.trim();
    const unit = slot.unit.trim();
    if (slotName === "" || slotCategory === "" || unit === "" || slot.amountText.trim() === "") {
      return { ok: false, field: "slots", message: SLOT_FIELDS_MESSAGE, slotIndex: index };
    }
    const amount = parseSlotAmount(slot.amountText);
    if (amount === null)
      return { ok: false, field: "slots", message: SLOT_AMOUNT_MESSAGE, slotIndex: index };
    if ((slot.catalogRef !== undefined && !isRecipeCatalogReference(slot.catalogRef)) ||
        slotName.length > (slot.catalogRef ? 200 : 60) || slotCategory.length > (slot.catalogRef ? 100 : 40))
      return {ok: false, field: "slots", message: "Review this ingredient's identity and labels.", slotIndex: index};
    if (slot.catalogRef !== undefined && !isRecipeUnit(unit))
      return {ok: false, field: "slots", message: "Choose a supported ingredient unit.", slotIndex: index};
    const identity = slot.catalogRef ? `${slot.catalogRef.catalogId}/${slot.catalogRef.productId}` : slotName.toLowerCase();
    if (seen.has(identity)) {
      return { ok: false, field: "slots", message: SLOT_DUPLICATE_MESSAGE, slotIndex: index };
    }
    seen.add(identity);
    const notes = slot.notes.trim();
    slots.push({
      ...(slot.catalogRef === undefined ? {} : {catalogRef: {...slot.catalogRef}}),
      name: slotName,
      category: slotCategory,
      amount,
      unit,
      ...(notes === "" ? {} : { notes }),
    });
  }
  if (!allowEmptyTargets && !allLocations && selected.size === 0) return { ok: false, field: "locations", message: RECIPE_LOCATIONS_MISSING };
  return { ok: true, body: { name, category, adoption, ...(allLocations ? { allLocations: true } : { locationIds: [...selected] }), slots, ...instructionsOf(draft) } };
}

export const RECIPE_PUBLISH_FAILED_MESSAGE =
  "The recipe could not be published. Nothing was changed. Try again.";

/** Words for a failed recipe publish. The server's sentence for a known refusal is plain language. */
export function describeRecipePublishFailure(status: number, body: unknown): string {
  const record = typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
  const code = record["error"];
  const message = record["message"];
  if (status === 0) return "Could not reach the server. Check your connection and try again.";
  if (status === 403) return "Your role does not allow publishing recipes.";
  if (status === 404)
    return "One of those locations is no longer in your network. Reload and choose again.";
  if (
    (code === "publication_name_taken" || code === "invalid_request") &&
    typeof message === "string" &&
    message !== ""
  ) {
    return message;
  }
  return RECIPE_PUBLISH_FAILED_MESSAGE;
}

export type { PublicationDto, StateView };
